import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { getSkillAgentBrandCollections } from "../../api/Skills/skillAgentSearchesApi";

export function sortBrandsAlpha(brands = []) {
  return [...(Array.isArray(brands) ? brands : [])].sort((a, b) =>
    String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
      sensitivity: "base",
    }),
  );
}

function brandSources(brand) {
  if (Array.isArray(brand?.sources) && brand.sources.length) {
    return brand.sources;
  }
  return (brand?.urls || []).map((url) => ({
    url,
    label: menuLabelFromUrl(url),
    defaultSelected:
      /jacket|outerwear|coat|veste|chaqueta|blouson|cuir|abrigo/i.test(url),
  }));
}

/** Label estilo menú (no el slug crudo). */
export function menuLabelFromUrl(url) {
  const raw = String(url || "").toLowerCase();
  let path = raw;
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    /* keep */
  }
  if (/vestes-blousons/.test(path)) return "VESTES & BLOUSONS";
  if (/walk-x-schott|vestes-en-cuir/.test(path)) return "VESTES EN CUIR";
  if (/chaquetas/.test(path)) return "CHAQUETAS";
  if (
    /jackets-and-outerwear|mens-jackets-and-coats|mens-jackets-coats|kith-outerwear|mens-outerwear|men-jackets|coats-jackets|outerwear-jackets/.test(
      path,
    )
  ) {
    return "OUTERWEAR & JACKETS";
  }
  if (
    /\/jackets?(\/|$)|archive-jackets|denim-jackets|jackets-all|bomber-jackets/.test(
      path,
    )
  ) {
    return "JACKETS";
  }
  if (/outerwear/.test(path)) return "OUTERWEAR";
  if (
    /new-arrivals|new-in|novedades|nouveautes|mens-new|novedad|new-arrival/.test(
      path,
    ) ||
    /\/new(\/|$)/.test(path)
  ) {
    return "NEW ARRIVALS";
  }
  if (/best-sellers|bestseller/.test(path)) return "BEST SELLERS";
  if (/shop-all|\/all(\/|$)|all-items|available-items/.test(path))
    return "SHOP ALL";
  if (/tracksuits?|survet/.test(path)) return "TRACKSUITS";
  if (/coat|abrigo/.test(path)) return "COATS";
  return collectionLabel(url).toUpperCase();
}

/** Default: prefer defaultSelected sources; else all urls. */
export function buildDefaultUrlSelection(brands = [], brandIds = []) {
  const idSet = new Set(brandIds || []);
  const out = {};
  for (const b of brands || []) {
    if (!idSet.has(b.id)) continue;
    const sources = brandSources(b);
    const preferred = sources.filter((s) => s.defaultSelected);
    out[b.id] = (preferred.length ? preferred : sources)
      .map((s) => s.url)
      .filter((u) => /^https?:\/\//i.test(String(u || "").trim()));
  }
  return out;
}

/**
 * Drop non-http leftovers (e.g. label "Zalando") and rebuild from catalog if empty.
 */
export function healUrlSelection(
  urlSelection = {},
  brands = [],
  brandIds = [],
) {
  const idSet = new Set(brandIds || []);
  const next = {
    ...(urlSelection && typeof urlSelection === "object" ? urlSelection : {}),
  };
  for (const b of brands || []) {
    if (!idSet.has(b.id)) continue;
    const sel = Array.isArray(next[b.id]) ? next[b.id] : null;
    if (!sel) continue;
    const http = sel
      .map((u) => String(u || "").trim())
      .filter((u) => /^https?:\/\//i.test(u));
    if (!http.length) {
      const rebuilt = buildDefaultUrlSelection([b], [b.id])[b.id];
      if (rebuilt?.length) next[b.id] = rebuilt;
      else delete next[b.id];
    } else if (http.length !== sel.length) {
      next[b.id] = http;
    }
  }
  return next;
}

export function collectionLabel(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1] || u.hostname;
    return decodeURIComponent(last).replace(/-/g, " ");
  } catch {
    return String(url || "")
      .replace(/^https?:\/\//, "")
      .slice(0, 48);
  }
}

/**
 * Checklist de marcas + filtros/colecciones (labels del menú web).
 */
export default function SkillAgentBrandSourcesFields({
  brands = [],
  brandIds = [],
  urlSelection = {},
  onBrandIdsChange,
  onUrlSelectionChange,
  maxHeight = 320,
}) {
  const sorted = sortBrandsAlpha(brands);
  const [menuByBrand, setMenuByBrand] = useState({});
  const [loadingMenu, setLoadingMenu] = useState({});
  const [menuError, setMenuError] = useState({});

  const sourcesFor = (brand) => {
    if (Array.isArray(menuByBrand[brand.id]) && menuByBrand[brand.id].length) {
      return menuByBrand[brand.id];
    }
    return brandSources(brand);
  };

  const setBrandOn = (brand, on) => {
    const nextIds = on
      ? [...new Set([...brandIds, brand.id])]
      : brandIds.filter((id) => id !== brand.id);
    onBrandIdsChange?.(nextIds);
    const nextSel = { ...urlSelection };
    if (on) {
      const sources = sourcesFor(brand);
      const preferred = sources.filter((s) => s.defaultSelected);
      nextSel[brand.id] = (preferred.length ? preferred : sources).map(
        (s) => s.url,
      );
    } else {
      delete nextSel[brand.id];
    }
    onUrlSelectionChange?.(nextSel);
  };

  const setUrlOn = (brand, url, on) => {
    const sources = sourcesFor(brand);
    const current = Array.isArray(urlSelection[brand.id])
      ? urlSelection[brand.id]
      : sources.filter((s) => s.defaultSelected).map((s) => s.url);
    const nextUrls = on
      ? [...new Set([...current, url])]
      : current.filter((u) => u !== url);
    const nextSel = { ...urlSelection, [brand.id]: nextUrls };
    onUrlSelectionChange?.(nextSel);

    if (nextUrls.length && !brandIds.includes(brand.id)) {
      onBrandIdsChange?.([...brandIds, brand.id]);
    } else if (!nextUrls.length && brandIds.includes(brand.id)) {
      onBrandIdsChange?.(brandIds.filter((id) => id !== brand.id));
      const cleared = { ...nextSel };
      delete cleared[brand.id];
      onUrlSelectionChange?.(cleared);
    }
  };

  const loadWebMenu = async (brand) => {
    setLoadingMenu((p) => ({ ...p, [brand.id]: true }));
    setMenuError((p) => ({ ...p, [brand.id]: "" }));
    try {
      const data = await getSkillAgentBrandCollections(brand.id);
      const cols = Array.isArray(data?.collections) ? data.collections : [];
      if (!cols.length) {
        setMenuError((p) => ({
          ...p,
          [brand.id]: "No se pudo leer el menú (¿no es Shopify?)",
        }));
        return;
      }
      setMenuByBrand((p) => ({ ...p, [brand.id]: cols }));
      if (brandIds.includes(brand.id)) {
        const preferred = cols.filter((s) => s.defaultSelected);
        onUrlSelectionChange?.({
          ...urlSelection,
          [brand.id]: (preferred.length ? preferred : cols.slice(0, 3)).map(
            (s) => s.url,
          ),
        });
      }
    } catch (err) {
      setMenuError((p) => ({
        ...p,
        [brand.id]: err?.response?.data?.error || "Error al cargar menú",
      }));
    } finally {
      setLoadingMenu((p) => ({ ...p, [brand.id]: false }));
    }
  };

  const selectAll = () => {
    onBrandIdsChange?.(sorted.map((b) => b.id));
    onUrlSelectionChange?.(
      buildDefaultUrlSelection(
        sorted,
        sorted.map((b) => b.id),
      ),
    );
  };

  const selectNone = () => {
    onBrandIdsChange?.([]);
    onUrlSelectionChange?.({});
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.75 }}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          Marcas y filtros web ({brandIds.length}/{sorted.length})
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            size="small"
            sx={{ textTransform: "none", fontWeight: 700 }}
            onClick={selectAll}
          >
            Todas
          </Button>
          <Button
            size="small"
            color="warning"
            sx={{ textTransform: "none", fontWeight: 700 }}
            onClick={selectNone}
          >
            Quitar todas
          </Button>
        </Stack>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.75 }}
      >
        Desmarca una marca para no rastrearla en este SEARCH. «Quitar todas»
        deja solo las webs propias. En Shopify: «Menú web» refresca colecciones
        vivas; el resto usa catálogo curado.
      </Typography>
      <Box
        sx={{
          maxHeight,
          overflow: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          px: 1,
        }}
      >
        {sorted.map((b) => {
          const brandOn = brandIds.includes(b.id);
          const sources = sourcesFor(b);
          const selectedUrls = Array.isArray(urlSelection[b.id])
            ? urlSelection[b.id]
            : brandOn
              ? sources.filter((s) => s.defaultSelected).map((s) => s.url)
              : [];
          return (
            <Box
              key={b.id}
              sx={{
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "divider",
                "&:last-child": { borderBottom: "none" },
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
              >
                <FormControlLabel
                  sx={{ alignItems: "flex-start", m: 0, flex: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={brandOn}
                      indeterminate={
                        brandOn &&
                        selectedUrls.length > 0 &&
                        selectedUrls.length < sources.length
                      }
                      onChange={(e) => setBrandOn(b, e.target.checked)}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ pt: 0.25 }}
                    >
                      {b.name}
                    </Typography>
                  }
                />
                {b.shopifyMenu !== false &&
                (b.shopifyMenu === true ||
                  (b.urls || []).some((u) => /\/collections\//i.test(u)) ||
                  (b.sources || []).some((s) =>
                    /\/collections\//i.test(s.url || s),
                  )) ? (
                  <Button
                    size="small"
                    sx={{ textTransform: "none", mt: 0.25, flexShrink: 0 }}
                    disabled={!!loadingMenu[b.id]}
                    onClick={() => loadWebMenu(b)}
                    startIcon={
                      loadingMenu[b.id] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : null
                    }
                  >
                    Menú web
                  </Button>
                ) : null}{" "}
              </Stack>
              {menuError[b.id] ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ pl: 3.5, display: "block" }}
                >
                  {menuError[b.id]}
                </Typography>
              ) : null}
              {sources.length > 0 && (
                <Stack spacing={0} sx={{ pl: 3.5, pb: 0.25 }}>
                  {sources.map((s) => {
                    const u = s.url;
                    const checked = selectedUrls.includes(u);
                    return (
                      <FormControlLabel
                        key={u}
                        sx={{ m: 0, alignItems: "flex-start" }}
                        control={
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={!brandOn && !checked}
                            onChange={(e) => setUrlOn(b, u, e.target.checked)}
                            sx={{ py: 0.25 }}
                          />
                        }
                        label={
                          <Box sx={{ pt: 0.35, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              display="block"
                              sx={{
                                lineHeight: 1.2,
                                letterSpacing: 0.02,
                                textTransform: "uppercase",
                              }}
                            >
                              {s.label || menuLabelFromUrl(u)}
                              {s.productsCount != null
                                ? ` · ${s.productsCount}`
                                : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              noWrap
                              title={u}
                              sx={{ maxWidth: 420 }}
                            >
                              {u.replace(/^https?:\/\//, "")}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </Stack>
              )}
            </Box>
          );
        })}
        {!sorted.length && (
          <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
            Catálogo de marcas no disponible.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
